import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FiDownload } from 'react-icons/fi';

export default function MasterDataPage() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({});
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [campaignId, setCampaignId] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await api.get(`/admin/master-data?page=${page}&limit=50&search=${search}`);
      setData(result.data);
      setTotal(result.total);
      setPages(result.pages);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    api.get('/admin/master-data/stats').then(({ data }) => setStats(data));
    api.get('/admin/campaigns?limit=100').then(({ data }) => setCampaigns(data.campaigns || []));
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return toast.warning('Select a file');
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (campaignId) formData.append('campaignId', campaignId);
    try {
      const { data } = await api.post('/admin/master-data/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(data.message);
      setShowUpload(false);
      setFile(null);
      fetchData();
      api.get('/admin/master-data/stats').then(({ data }) => setStats(data));
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/admin/master-data/export-excel${search ? `?search=${search}` : ''}`, {
        responseType: 'blob',
        timeout: 300000 // 5 min timeout for large exports
      });
      const contentType = response.headers['content-type'];
      const isZip = contentType?.includes('zip');
      const ext = isZip ? 'zip' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MasterData_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(isZip ? 'Data exported as multiple files (ZIP)' : 'Data exported successfully');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Data Hub</h1>
          <p className="page-subtitle">{total.toLocaleString()} records</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={handleExport} disabled={exporting || total === 0}>
            <FiDownload /> {exporting ? 'Exporting...' : 'Export Excel'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>Upload Data</button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-icon purple"><FiDownload /></div>
          <div className="stat-card-value">{(stats.total || 0).toLocaleString()}</div>
          <div className="stat-card-label">Total Records</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon green">✓</div>
          <div className="stat-card-value">{(stats.withCampaign || 0).toLocaleString()}</div>
          <div className="stat-card-label">Assigned to Campaign</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-icon amber">◷</div>
          <div className="stat-card-value">{(stats.withoutCampaign || 0).toLocaleString()}</div>
          <div className="stat-card-label">Unassigned</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <input type="text" className="form-input" placeholder="Search by mobile, name, email..."
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ maxWidth: 360 }} />
      </div>

      <div className="card">
        {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Mobile</th><th>Name</th><th>Email</th><th>Address</th><th>Uploaded</th></tr>
              </thead>
              <tbody>
                {data.map(row => (
                  <tr key={row._id}>
                    <td style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>{row.mobile}</td>
                    <td>{row.name}</td>
                    <td>{row.email}</td>
                    <td>{row.address}</td>
                    <td>{new Date(row.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
                {data.length === 0 && <tr><td colSpan="5" className="empty-state">No data found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="pagination">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            {[...Array(Math.min(pages, 10))].map((_, i) => (
              <button key={i} className={page === i + 1 ? 'active' : ''} onClick={() => setPage(i + 1)}>{i + 1}</button>
            ))}
            {pages > 10 && <button disabled>...</button>}
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>›</button>
          </div>
        )}
      </div>

      {showUpload && (
        <div className="modal-overlay" onClick={() => setShowUpload(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Master Data</h2>
              <button className="modal-close" onClick={() => setShowUpload(false)}>×</button>
            </div>
            <form onSubmit={handleUpload}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">File (CSV / Excel)</label>
                  <input type="file" className="form-input" accept=".csv,.xlsx,.xls"
                    onChange={e => setFile(e.target.files[0])} required style={{ padding: 8 }} />
                  <small style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                    Expected columns: mobile, name, email, address
                  </small>
                </div>
                <div className="form-group">
                  <label className="form-label">Assign to Campaign (Optional)</label>
                  <select className="form-select" value={campaignId} onChange={e => setCampaignId(e.target.value)}>
                    <option value="">None</option>
                    {campaigns.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowUpload(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
